// =============================================================================
// LIGA AJUPAM — Guion del Simulacro (v6.11.73)
// =============================================================================
// El contenido del onboarding lúdico, como dato y no como JSX. Especificación
// completa y decisiones de alcance: `docs/features/ONBOARDING_SIMULACRO.md`.
//
// CÓMO ESCRIBIR ACÁ (v6.11.73, después de la primera revisión de la organización)
//
//   · Es UN SOLO PARTIDO, de principio a fin. Cada escena continúa la anterior y
//     lo dice: "Quedaron el jueves", "Llega el día". Si una escena necesita una
//     situación nueva, se explica el salto — nunca aparece de la nada.
//   · Frases cortas. Se lee en el celular, parado, con poca paciencia.
//   · Cada dato que se menciona se usa después. El punto de oro se explicaba y
//     no se preguntaba nunca; ahora tiene su propia decisión. Lo que no se usa,
//     se saca.
//   · Los ejemplos con números tienen que dar bien. La versión anterior decía
//     "miércoles a la tarde, partido el jueves 21:00 → menos de 5 horas hábiles"
//     y eso son 17 horas hábiles: la escena enseñaba la regla con un caso que no
//     la cumple. Si hay una cuenta, hacerla.
//   · Cuando la situación es una cuenta (un marcador, unos minutos de
//     tolerancia), va un esquema y no un párrafo. Ver `SimulacroVisual.tsx`.
//
// Por qué los textos llevan `{{placeholders}}`: los números del reglamento
// cambian de una edición a la otra, y `get_public_rulebook` ya los devuelve
// resueltos por liga. Escribir "5 horas hábiles" en duro significa que la 6ª
// edición enseñe el número de la 5ª sin que nadie se entere.
//
// Qué está deliberadamente AFUERA del guion, y por qué (los dos por pedido de la
// organización, para que no vuelvan a entrar sin querer en una revisión futura):
//
//   1. La no finalización del encuentro (§8): si el turno corta antes de
//      terminar el segundo set hay una ventana de 7 días y, si no se ponen de
//      acuerdo, sorteo game a game. Es otra mecánica y contarla junto al súper
//      tie-break mezcla dos situaciones que no se parecen.
//   2. Los días de gracia del arranque (`utils/gracePeriod.ts`), durante los
//      cuales cargar tarde no genera advertencia. El simulacro se juega justo
//      dentro de esa ventana: mencionarla enseña lo contrario de lo que se
//      busca, que el plazo es de una hora y punto.

import type { SimulacroVisual } from '../components/simulacro/SimulacroVisual';

/** Sanción que se arrastra hasta la pantalla de cierre. */
export type SimulacroSanction = 'ADVERTENCIA' | 'MULTA' | 'WO_EN_CONTRA';

export interface SimulacroOption {
  id: string;
  label: string;
  /**
   * `true` / `false` corrigen. `null` es para las escenas donde ninguna opción
   * está mal (E3: todo horario es válido si se acuerda entre partes) y lo que se
   * comprueba es si entienden qué están aceptando, no si aciertan.
   */
  correct: boolean | null;
  /** Qué pasa si elegís esto. Se muestra siempre, se acierte o no. */
  outcome: string;
  sanction?: SimulacroSanction;
  /** true si esta decisión hace que el partido no sume. */
  losesMatch?: boolean;
}

export interface SimulacroBeat {
  id: string;
  prompt: string;
  /** Esquema que acompaña la pregunta, cuando la situación es una cuenta. */
  visual?: SimulacroVisual;
  options: SimulacroOption[];
}

export interface SimulacroScene {
  id: string;
  /** Número visible: "Escena 3 de 7". */
  number: number;
  title: string;
  /** Contexto narrativo. Soporta **negrita**. */
  setup: string;
  beats: SimulacroBeat[];
  /**
   * Artículo del reglamento al que remite la escena.
   *
   * `anchor` es el `id` que `PublicRulebook` genera con `slugifyHeading` sobre
   * el título del markdown publicado. Los valores de acá se sacaron corriendo
   * esa misma función sobre los títulos reales de los reglamentos en base, no a
   * ojo: "7.1 Fase Regular" da `71-fase-regular` porque el punto se cae con el
   * resto de los caracteres no alfanuméricos.
   *
   * Se usaron SÓLO títulos presentes en las tres ligas activas (`5ta-edicion`,
   * `sur`, `uco`), porque cada una publica su propio reglamento y el simulacro
   * es el mismo para todas. Varias escenas apuntan a la sección que contiene el
   * apartado en vez de al apartado mismo (p. ej. "Tardanzas" no es un título,
   * vive dentro de "Detalle de Infracciones"): `section` dice el apartado exacto
   * en pantalla y el ancla deja al lector lo más cerca posible.
   *
   * Si una edición futura renombra un título, el link sigue abriendo el
   * reglamento y simplemente no salta — degrada, no rompe.
   */
  rulebook: { section: string; anchor?: string };
}

/**
 * Pantalla de entrada (v6.11.76, pedido del equipo).
 *
 * Dos trabajos, en este orden: primero picar, después explicar. Arrancar por
 * "esto es un onboarding de siete escenas" es la forma más rápida de que lo
 * cierren; arrancar por "¿la tenés clara?" es la forma de que lo abran. El
 * desafío no es adorno — es literalmente lo que el simulacro comprueba.
 *
 * El permiso para equivocarse va acá y no al final. Alguien que cree que lo
 * están evaluando contesta a la defensiva, y lo que buscamos es que se mande.
 */
export const SIMULACRO_INTRO = {
  // Sin volanta: la franja de arriba ya dice "MODO SIMULACRO" y repetirlo dos
  // centímetros más abajo sólo le roba el primer golpe de vista al título, que
  // es lo único que decide si esto se abre o se cierra.
  title: '¿La tenés tan clara como creés?',
  body:
    'Casi todos dicen que sí. Después llega la primera advertencia.\n\n' +
    'Esto es **un partido entero**, de principio a fin: arreglarlo con el rival, cargarlo, ' +
    'jugarlo y reportar el resultado. **Siete escenas, cinco minutos.** En cada una vas a tener ' +
    'que decidir algo, y varias tienen trampa.\n\n' +
    'Equivocate todo lo que quieras: **nada de lo que hagas acá afecta a tu equipo, tus partidos ' +
    'ni la tabla**. Es exactamente para eso.\n\n' +
    'Al final te decimos dónde habrías terminado.',
  cta: 'Dale, empecemos',
  /** Se muestra chiquito abajo del botón. */
  footnote: 'Todo lo que se pregunta está en el reglamento de tu liga.',
};

export const SIMULACRO_SCENES: SimulacroScene[] = [
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'quedar',
    number: 1,
    title: '💬 Arreglar el partido',
    setup:
      'Te falta jugar con **Los Tuertos**. Les escribís por WhatsApp.\n\n' +
      'Contestan al toque: *"Buenísimo, cuando quieran"*.',
    rulebook: { section: '§6.1 — Autonomía en la Programación', anchor: '61-fase-regular' },
    beats: [
      {
        id: 'cerrar',
        prompt: '¿Qué les respondés?',
        options: [
          {
            id: 'concreto',
            label: '🎾 "Jueves 20:00 en el Club Norte, ¿va?"',
            correct: true,
            outcome:
              'Así se arregla un partido: **día, hora y cancha**.\n\n' +
              'La liga no te impone horarios. Pueden jugar cuando quieran, donde quieran. ' +
              'Por eso mismo, si vos no proponés algo concreto, no lo hace nadie.',
          },
          {
            id: 'vago',
            label: '🤷 "Dale, el jueves a la noche vemos"',
            correct: false,
            outcome:
              'Eso no es arreglar un partido.\n\n' +
              'No hay hora ni cancha. Nadie reserva el turno. El jueves a las nueve todavía se ' +
              'están escribiendo, y el partido no se juega.',
          },
          {
            id: 'pelota',
            label: '📵 "Cuando puedan me avisan"',
            correct: false,
            outcome:
              'Le pasaste la pelota al otro. Que te la va a devolver.\n\n' +
              'Así es como una pareja llega a octubre con seis partidos sin jugar.',
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'programar',
    number: 2,
    title: '📱 Cargarlo en la app',
    setup:
      'Quedaron: **jueves 20:00, Club Norte**.\n\n' +
      'Los cuatro saben cuándo y dónde. Estás por guardar el celular.',
    rulebook: { section: '§9 — Partidos No Registrados Correctamente', anchor: 'detalle-de-infracciones' },
    beats: [
      {
        id: 'registrar',
        prompt: '¿Hace falta cargarlo en la app, si ya lo hablaron?',
        options: [
          {
            id: 'cargarlo',
            label: '✅ Lo cargo ahora',
            correct: true,
            outcome:
              'Listo. Lo puede cargar cualquiera de los cuatro, y queda **pendiente hasta que el ' +
              'rival lo confirme**.\n\n' +
              'Recién ahí el partido existe para la liga.',
          },
          {
            id: 'no-hace-falta',
            label: '💬 No, ya nos pusimos de acuerdo por WhatsApp',
            correct: false,
            outcome:
              'Esto no es "no cuenta". Es **una advertencia**. ⚠️\n\n' +
              'Jugar un partido sin haberlo registrado antes está sancionado. Y aunque no lo ' +
              'estuviera: sin partido cargado no tenés dónde subir el resultado ni la foto.',
            sanction: 'ADVERTENCIA',
            losesMatch: true,
          },
          {
            id: 'despues',
            label: '⏳ Lo cargo después, junto con el resultado',
            correct: false,
            outcome:
              'Tarde. El partido tiene que estar cargado **antes** de jugarse.\n\n' +
              'Cargarlo después es la misma advertencia que no cargarlo. ⚠️',
            sanction: 'ADVERTENCIA',
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'horario',
    number: 3,
    title: '📅 Se te complica el día',
    setup:
      'Martes. El partido es el jueves, pero te avisan del trabajo que ese día viajás.\n\n' +
      'No podés jugar. Les escribís a Los Tuertos para avisarles.',
    rulebook: { section: '§8 — Modificaciones de Partidos · §9 — Cancelación Anticipada', anchor: 'disposiciones-generales-del-partido' },
    beats: [
      {
        id: 'cambiar-dia',
        prompt: 'Ya avisaste por WhatsApp. ¿Qué más hay que hacer?',
        options: [
          {
            id: 'cancelo',
            label: '✅ Cancelo el partido en la app, así después lo reprogramamos',
            correct: true,
            outcome:
              'Así es. **El que no puede jugar es el que cancela.**\n\n' +
              'Un partido no se "mueve": se cancela y se vuelve a programar. Y para programarlo de ' +
              'nuevo no hace falta que seas vos — puede hacerlo cualquiera de los cuatro, cuando ' +
              'encuentren un día que les sirva.\n\n' +
              '⚠️ Una sola cosa a tener en cuenta: cancelar es gratis porque faltan días. Si ' +
              'cancelás con menos de {{wo_cancellation_hours}} horas hábiles, es **WO igual, aunque ' +
              'avises**. Por eso conviene hacerlo apenas te enterás.',
          },
          {
            id: 'solo-whatsapp',
            label: '💬 Nada más, ya les avisé',
            correct: false,
            outcome:
              'Para tus rivales sí. **Para la liga, no.**\n\n' +
              'El partido sigue programado en la app: sigue figurando para el jueves y sigue ' +
              'esperando un resultado que nunca va a llegar. Avisar y cancelar son dos cosas ' +
              'distintas, y hay que hacer las dos.',
          },
          {
            id: 'espero',
            label: '🤞 Espero a ver si se destraba el viaje',
            correct: false,
            outcome:
              'Es lo más tentador y lo más caro.\n\n' +
              'Si esperás hasta el miércoles a la noche, ya estás dentro de las ' +
              '{{wo_cancellation_hours}} horas hábiles previas al partido. Ahí cancelar **ya es WO**, ' +
              'y el reglamento aclara que se aplica *sin importar la gravedad del caso*.\n\n' +
              'Cuando la duda aparece, cancelá. Reprogramar es fácil; sacarse un WO de encima, no.',
          },
        ],
      },
      {
        id: 'reprogramar',
        prompt:
          'Cancelado. Unos días después encuentran un hueco que le sirve a los cuatro.\n\n' +
          '¿Quién lo vuelve a programar?',
        visual: {
          kind: 'pasos',
          pasos: [
            'El que no podía jugar cancela el partido en la app 📱',
            'La fecha queda libre para las dos parejas',
            'Cualquiera de los cuatro lo programa de nuevo 🎾',
          ],
        },
        options: [
          {
            id: 'cualquiera',
            label: '👥 Cualquiera de los cuatro',
            correct: true,
            outcome:
              'Exacto. No hay un "dueño" del partido: lo carga el que tenga el celular a mano, y ' +
              'el otro equipo lo confirma.',
          },
          {
            id: 'el-que-cancelo',
            label: '🙋 El que canceló, que para eso lo canceló',
            correct: false,
            outcome:
              'No hace falta. Cancelar y programar son cosas separadas: **el que cancela es el que ' +
              'tiene el problema, pero programar lo puede hacer cualquiera de los cuatro.**',
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'llegar',
    number: 4,
    title: '🚗 Llegar a la cancha',
    setup:
      'Llega el día del partido. **20:00** en el Club Norte.\n\n' +
      'Salís justo, hay tránsito, y encima el predio avisa que la cancha se libera 20:10.',
    rulebook: { section: '§9 — Tardanzas · §9 — Partidos No Disputados', anchor: 'detalle-de-infracciones' },
    beats: [
      {
        id: 'tolerancia',
        prompt: 'Llegás 20:20. ¿Qué corresponde?',
        visual: {
          kind: 'linea',
          hitos: [
            { hora: '20:00', texto: 'Horario del partido — acá arranca el reloj', tono: 'ok' },
            { hora: '20:15', texto: 'Se termina la tolerancia', tono: 'limite' },
            { hora: '20:20', texto: 'Llegás vos', tono: 'mal' },
          ],
        },
        options: [
          {
            id: 'quince',
            label: '😬 Me pasé: eran 15 minutos desde las 20:00',
            correct: true,
            outcome:
              'Exacto. La tolerancia es de **15 minutos y corre desde el horario del partido**.\n\n' +
              'Si llegás dentro de la tolerancia jugás igual, sólo que calentás menos.',
          },
          {
            id: 'cancha-tarde',
            label: '🤷 La cancha se liberó 20:10, el reloj corre desde ahí',
            correct: false,
            outcome:
              'No. Esto es lo que más discusiones genera y el reglamento lo cierra: **no se da ' +
              'tiempo adicional si el predio libera tarde**.\n\n' +
              'El reloj arranca a las 20:00 pase lo que pase. A las 20:15 ya te pasaste. ⚠️',
            sanction: 'WO_EN_CONTRA',
            losesMatch: true,
          },
        ],
      },
      {
        id: 'alarma',
        prompt:
          'Llegaste a horario. El turno es hasta las 21:30.\n\n' +
          'Antes de entrar, ¿ponés una alarma para el final del turno?',
        options: [
          {
            id: 'pongo',
            label: '⏰ Sí, la pongo',
            correct: true,
            outcome: 'El reglamento lo recomienda. Todavía no se ve para qué. En un rato sí.',
          },
          {
            id: 'no-pongo',
            label: '🙄 No hace falta, uno mira el reloj',
            correct: false,
            outcome: 'Acordate de esta respuesta. Vas a volver a ella en dos escenas.',
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'foto',
    number: 5,
    title: '📸 La foto de los cuatro',
    setup:
      'Antes de empezar va la foto del partido.\n\n' +
      'No es para el Instagram: la app le compara la cara a cada uno contra su **foto de perfil**, ' +
      'y así se controla que jueguen los que tienen que jugar.',
    rulebook: { section: '§7.1 — Verificación de identidad', anchor: '71-fase-regular' },
    beats: [
      {
        id: 'cual-sirve',
        prompt: '¿Cuál de estas tres sirve?',
        options: [
          {
            id: 'buena',
            label: '✅ Los cuatro de frente, en la cancha',
            correct: true,
            outcome:
              'Esa. Los cuatro, en la cancha, con las caras visibles.\n\n' +
              'Diez segundos que te ahorran una denuncia por jugador cambiado.',
          },
          {
            id: 'tres',
            label: '🙋 Los tres que llegaron, el cuarto estaba estacionando',
            correct: false,
            outcome:
              'No sirve. Tienen que estar **los cuatro**.\n\n' +
              'La foto es el registro de quién jugó ese partido. Con tres, no registra nada.',
          },
          {
            id: 'espaldas',
            label: '🚶 Los cuatro entrando a la cancha, de espaldas',
            correct: false,
            outcome:
              'No sirve. Si no se ven las caras, la app no puede compararlas con los perfiles ' +
              'y la verificación rebota.',
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'jugar',
    number: 6,
    title: '🎾 El partido',
    setup: 'Arrancó. Y arrancó peleado.',
    rulebook: { section: '§8 — Normativa de Juego · Definición del Tercer Set', anchor: 'definicion-del-tercer-set' },
    beats: [
      {
        id: 'punto-de-oro',
        prompt:
          'Primer set, juego largo: **40-40**.\n\n' +
          'Tu compañero te mira. ¿Qué se juega?',
        options: [
          {
            id: 'oro',
            label: '🥇 Un solo punto y se define el juego',
            correct: true,
            outcome:
              'Punto de oro. **No hay ventajas**: en 40-40 se define con un punto.\n\n' +
              'Y lo que casi nadie sabe: **elige lado la pareja que resta**.',
          },
          {
            id: 'ventajas',
            label: '🎾 Ventajas, hasta que alguien saque dos de diferencia',
            correct: false,
            outcome:
              'No. En esta liga se juega con **punto de oro**: en 40-40 va un solo punto, sin ' +
              'ventajas, y elige lado la pareja que resta.',
          },
        ],
      },
      {
        id: 'tercer-set',
        prompt:
          'Se fueron 1-1 en sets, así que el tercero es **súper tie-break a 15**, con dos de diferencia.\n\n' +
          'Van 12-11 arriba... y suena la alarma. ⏰ **Se terminó el turno.**',
        visual: {
          kind: 'marcador',
          columnas: ['Set 1', 'Set 2', 'Súper'],
          destacar: 2,
          filas: [
            { equipo: 'Tu pareja', valores: [6, 4, 12], vos: true },
            { equipo: 'Los Tuertos', valores: [4, 6, 11] },
          ],
          nota: '⏰ Suena la alarma justo acá',
        },
        options: [
          {
            id: 'termina-ahi',
            label: '🏆 Termina ahí: ganamos 12-11',
            correct: true,
            outcome:
              'Correcto. **El turno corta el partido y gana el que va arriba.**\n\n' +
              'Un detalle importante: en la app se carga **7-6**, no 12-11. El súper tie-break se ' +
              'registra siempre así, sea cual sea la diferencia.',
          },
          {
            id: 'seguir',
            label: '🎾 Seguimos hasta 15, el predio nos deja la cancha',
            correct: false,
            outcome:
              'No, y es la trampa más cara del reglamento.\n\n' +
              'Cuando suena la alarma el resultado queda como está *"independientemente de si el ' +
              'predio permite continuar el juego por más tiempo"*. Lo que sigan jugando **no cuenta**.',
          },
          {
            id: 'desempate-mal',
            label: '⚡ Jugamos un punto de desempate',
            correct: false,
            outcome:
              'Eso es para otra situación. Acá no hay nada que desempatar: van 12-11, ya hay un ' +
              'ganador.',
          },
        ],
      },
      {
        id: 'repregunte',
        prompt: 'Una más, la misma escena pero con otro marcador. ¿Y si al sonar la alarma iban **11-11**?',
        visual: {
          kind: 'marcador',
          columnas: ['Set 1', 'Set 2', 'Súper'],
          destacar: 2,
          filas: [
            { equipo: 'Tu pareja', valores: [6, 4, 11], vos: true },
            { equipo: 'Los Tuertos', valores: [4, 6, 11] },
          ],
          nota: '⏰ Misma alarma, marcador empatado',
        },
        options: [
          {
            id: 'punto-mas',
            label: '⚡ Se juega un punto más y se define ahí',
            correct: true,
            outcome:
              'Esa. **El turno corta el partido, pero no puede dejarlo empatado.**\n\n' +
              'Un punto y listo. Acá se ve para qué servía la alarma: si no sabés a qué hora se ' +
              'termina el turno, no sabés que estás jugando el último punto.',
          },
          {
            id: 'empate',
            label: '🤝 Queda empatado y se registra así',
            correct: false,
            outcome:
              'No puede ser: **todo partido tiene que terminar con un ganador**. El empate no es ' +
              'un resultado posible.',
          },
          {
            id: 'seguir-dos',
            label: '🎾 Se sigue hasta sacar dos de diferencia',
            correct: false,
            outcome: 'Eso sería seguir jugando después del turno, que es justo lo que no se puede.',
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'resultado',
    number: 7,
    title: '📲 Cargar el resultado',
    setup:
      'Ganaron. Se saludan, salen de la cancha.\n\n' +
      'Desde que termina el partido hay **una hora** para cargar el resultado con su foto. ' +
      'Llegás a tu casa y ves que Los Tuertos ya lo cargaron.',
    rulebook: { section: '§7.1 — Reporte de resultado', anchor: '71-fase-regular' },
    beats: [
      {
        id: 'resultado-rival',
        prompt:
          'Lo mirás y **está mal**: lo cargaron al revés. Así como está, **el partido lo ganaron ellos**.\n\n' +
          'Tenés 15 minutos para confirmarlo o avisar que no estás de acuerdo.',
        visual: {
          kind: 'marcador',
          columnas: ['Set 1', 'Set 2', 'Súper'],
          filas: [
            { equipo: 'Tu pareja', valores: [4, 6, 6], vos: true },
            { equipo: 'Los Tuertos', valores: [6, 4, 7] },
          ],
          nota: '❌ Ganaron ustedes 6-4 / 4-6 / 7-6. Está cargado justo al revés.',
        },
        options: [
          {
            id: 'aviso',
            label: '✋ Aviso el desacuerdo para que lo revise la organización',
            correct: true,
            outcome:
              'Eso. Los 15 minutos no son para pensarlo: son para avisar.\n\n' +
              'Puede haber sido un error de dedo, no hace falta salir a pelear. Pero hay que ' +
              'avisar **ahora**.',
          },
          {
            id: 'acepto',
            label: '🤷 Lo acepto, no quiero hacer lío',
            correct: false,
            outcome:
              'Acabás de regalar un partido que ganaste.\n\n' +
              'Los {{points_win_2_1}} puntos de la victoria se los quedan ellos y a vos te queda ' +
              '{{points_lose_1_2}}. Evitar el lío te salió carísimo.',
            losesMatch: true,
          },
          {
            id: 'dejo-vencer',
            label: '😴 Lo dejo pasar, mañana lo arreglo',
            correct: false,
            outcome:
              'Mañana ya está confirmado. **Si no hacés nada en 15 minutos, queda como está.**\n\n' +
              'No hacer nada también es una decisión.',
            losesMatch: true,
          },
        ],
      },
      {
        id: 'foto-olvidada',
        prompt:
          'Arreglado el resultado, queda un detalle: **la foto no la subió nadie**. La sacaste vos y ' +
          'está en tu celular.',
        options: [
          {
            id: 'ahora',
            label: '📤 La subo ahora, desde el partido',
            correct: true,
            outcome:
              'Listo, y ahí se termina el trámite. Si la foto entra dentro de la hora, no pasa nada.',
          },
          {
            id: 'despues',
            label: '🛋️ Después la subo, cuando me acuerde',
            correct: false,
            outcome:
              'Tenés una hora. Después de eso salta la advertencia por olvido de foto — y acá está ' +
              'lo importante: **le llega a los dos equipos**. ⚠️\n\n' +
              'Te la olvidaste vos y se la comió también el rival, que no tuvo nada que ver.',
            sanction: 'ADVERTENCIA',
          },
        ],
      },
    ],
  },
];

export const SIMULACRO_TOTAL_SCENES = SIMULACRO_SCENES.length;

/**
 * Versión del guion. Si algún día se reescribe en serio y conviene que quien ya
 * lo hizo lo vuelva a hacer, se bumpea acá y el flag de completado deja de
 * alcanzar. Ver `docs/features/ONBOARDING_SIMULACRO.md`.
 */
export const SIMULACRO_VERSION = 1;
